// «Персонаж» в виджете: ЕДИНАЯ капсула шапки (part='head') + кнопка-вход в
// ряду слотов десктопа (part='entry').
//
// Капсула — единственный вход/статус во ВСЕХ шапках (страничный /generate и
// виджеты /bots): «кнопку не менять, а трансформировать между состояниями»
// (решение владельца) — один DOM-элемент живёт постоянно, до выбора это
// силуэт + «Персонаж» + NEW, после выбора ТОТ ЖЕ кружок заполняется лицом,
// текст становится именем, справа появляется «×». Прежняя пара «иконка входа
// + бейдж строкой» и её badge-разметка удалены: рассинхрон поведений между
// поверхностями вскрыл владелец.
//
// Видимость входа компонент считает САМ (useGenerate + useAuth): раньше
// правило пробрасывалось пропом из трёх мест и дублировалось.
//
// Состояние берём из модульного стора (useCharacterSelection), действия шлём
// событиями: применение/снятие держит useSceneApply в UploadsRow, и
// звать его вторым экземпляром нельзя — внутри эффекты пере-плана.
import { openScenePicker, requestSceneRemove, sceneCharacter, useScene } from '../../../api/sceneSelection'
import { characterEntryVisible } from '../../../utils/characterFit'
import { useGenerate } from '../../../context/GenerateContext'
import { useAuth } from '../../../context/AuthContext'

interface Props {
	/** 'head' — капсула-трансформер шапки; 'entry' — кнопка в ряду слотов
	 *  (десктоп; на мобиле CSS прячет — вход там несёт капсула). */
	part?: 'entry' | 'head'
	/** Подпись-подсказка от карточки (админ, видео-поля 2026-08-20) —
	 *  замещает дефолтный title капсулы. */
	hint?: string
}

export default function CharacterRow({ part, hint }: Props) {
	// Капсула живёт в пайпе тренда: там в сцене один ассет — герой (персонаж).
	const scene = useScene()
	const hero = sceneCharacter(scene)
	const charSel = hero ? { name: hero.name, avatarUrl: hero.avatarUrl, blocked: !!scene?.blocked } : null
	const gen = useGenerate()
	const { user } = useAuth()
	const openCharacterPicker = () => openScenePicker({ kind: 'character', mode: 'pipe' })
	const requestCharacterClear = () => requestSceneRemove(hero?.id)

	const entryVisible = characterEntryVisible({
		isAuthed: !!user,
		hasSelection: !!charSel,
		fitMode: gen.selectedModel?.characterFit?.mode,
		isAudio: gen.isAudio,
		isPresentation: gen.isPresentation,
	})

	if (part === 'head') {
		if (!charSel && !entryVisible) return null
		return (
			// Овал ОДИН (фон на контейнере): «×» живёт ВНУТРИ него — отдельный
			// кружок рядом выглядел оторванным (скрин владельца). Внутри две
			// слитые кнопки (выбрать/сменить + снять) — вложенных button нет.
			<span className={`char-head${charSel ? ' char-head--filled' : ''}${charSel?.blocked ? ' char-head--blocked' : ''}`}>
				<button
					type="button"
					className="char-head__pick"
					onClick={openCharacterPicker}
					aria-label={charSel ? `Аватар: ${charSel.name} — сменить` : 'Аватар'}
					title={hint || (charSel ? 'Сменить аватар' : 'Аватар')}
				>
					<span className="char-head__ava">
						{charSel?.avatarUrl ? (
							// key: смена персонажа ремаунтит <img> — «проявление» лица
							// проигрывается заново (animation на маунт).
							<img key={charSel.avatarUrl} src={charSel.avatarUrl} alt="" />
						) : charSel ? (
							<span className="char-head__ava-letter">{charSel.name.slice(0, 1).toUpperCase()}</span>
						) : (
							<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden="true">
								<circle cx="12" cy="8" r="3.4" />
								<path d="M5 20c1.3-3.4 3.9-5 7-5s5.7 1.6 7 5" />
							</svg>
						)}
					</span>
					<span className="char-head__label">{charSel ? charSel.name : 'Аватар'}</span>
					{/* Акцент на новой фиче (решение владельца); снять после закрепления. */}
					{!charSel && <span className="char-entry-btn__new" aria-hidden="true">NEW</span>}
				</button>
				{charSel && (
					<button type="button" className="char-head__x" aria-label="Убрать аватар" onClick={requestCharacterClear}>×</button>
				)}
			</span>
		)
	}

	// Вход в РЯДУ слотов (десктоп; --row скрыт на мобиле — там капсула шапки).
	if (!entryVisible) return null
	return (
		<button
			type="button"
			className="char-entry-btn char-entry-btn--row"
			onClick={openCharacterPicker}
			aria-label="Аватар"
		>
			<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden="true">
				<circle cx="12" cy="8" r="3.4" />
				<path d="M5 20c1.3-3.4 3.9-5 7-5s5.7 1.6 7 5" />
			</svg>
			{/* Подпись — отдельным узлом: на мобиле кнопка остаётся прежней
			    компактной капсулой (силуэт + NEW), и текст там прячется CSS. */}
			<span className="char-entry-btn__label">Аватар</span>
			<span className="char-entry-btn__new" aria-hidden="true">NEW</span>
		</button>
	)
}
